import { IBankAccount, useBankAccount, useBusinessEntity } from '@commenda/api';
import { usePlaidButton } from '@commenda/api/plaid';
import {
  Alert,
  Button,
  CountryCode,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  FlagComponent,
  IconButton,
  Icons,
} from '@commenda/kakashi';
import { useRef, useState } from 'react';
import { RadioCardKS } from '../components';
import {
  BankAccountFormNew,
  BankAccountFormNewHandle,
} from './BankAccountFormNew';

export const AddBankAccountModal = ({
  corporationId,
  isShown,
  onClose,
  addAsRemittanceAccount,
  onSaveBankAccount,
  preventCloseOnOutsideClick,
  defaultCountry,
  showCountrySelector = true,
  alertText,
  disablePlaid = false,
  existingBankAccount,
}: {
  corporationId: number;
  isShown: boolean;
  addAsRemittanceAccount?: boolean;
  onClose: (method?: 'MANUAL' | 'EXTERNAL') => void;
  onSaveBankAccount?: (data: {
    method?: 'MANUAL' | 'EXTERNAL';
    bankAccountsIds: string[];
  }) => void;
  preventCloseOnOutsideClick?: boolean;
  /** ISO 3166-1 alpha-2 code (e.g. 'US'). Omit to fall back to the corporation's incorporation country. */
  defaultCountry?: string;
  showCountrySelector?: boolean;
  alertText?: string;
  disablePlaid?: boolean;
  /** When provided, opens directly in edit mode for an existing bank account. */
  existingBankAccount?: IBankAccount;
}) => {
  const { mutate: refetchBankAccounts } = useBankAccount({ corporationId });

  const { ready, handlePlaidOpen } = usePlaidButton({
    nextAuthToken: undefined,
    corporationId,
    onSuccess: async (bankAccountsIds: string[]) => {
      await refetchBankAccounts();
      if (onSaveBankAccount) {
        onSaveBankAccount({ method: 'EXTERNAL', bankAccountsIds });
      }
    },
    setNotification: () => {},
  });

  const { data: businessEntity } = useBusinessEntity(corporationId ?? 0);
  const entityName = businessEntity?.generalCorporationInfo.legalName ?? '';
  const countryCode = (businessEntity?.generalCorporationInfo
    .incorporationCountry ?? null) as CountryCode | null;

  const [view, setView] = useState<'CONNECTION_OPTIONS' | 'MANUAL_CONNECTION'>(
    disablePlaid || existingBankAccount
      ? 'MANUAL_CONNECTION'
      : 'CONNECTION_OPTIONS'
  );
  const [connectionMethod, setConnectionMethod] = useState<
    'MANUAL' | 'EXTERNAL'
  >('MANUAL');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<BankAccountFormNewHandle>(null);

  const handleClose = () => {
    setView(
      disablePlaid || existingBankAccount
        ? 'MANUAL_CONNECTION'
        : 'CONNECTION_OPTIONS'
    );
    onClose();
  };

  const onContinue = () => {
    if (connectionMethod === 'MANUAL') {
      setView('MANUAL_CONNECTION');
    } else if (ready) {
      onClose('EXTERNAL');
      handlePlaidOpen();
    }
  };

  // ── Step 1: Connection options — stays as a regular modal ─────────────────
  if (view === 'CONNECTION_OPTIONS') {
    return (
      <Dialog open={isShown} onOpenChange={handleClose}>
        <DialogContent
          className="min-w-[600px]"
          preventCloseOnOutsideClick={preventCloseOnOutsideClick}
          hideCloseIcon={preventCloseOnOutsideClick}
        >
          <DialogHeader className="border-b-0 flex flex-col items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-bg-primary flex items-center justify-center border border-border-primary">
              <Icons.Bank className="size-5 text-fg-secondary" />
            </div>
            <div className="flex flex-col gap-1">
              <DialogTitle>Select a method</DialogTitle>
              <p className="txt-small-regular text-text-tertiary">
                You can add your bank accounts in two ways.
              </p>
            </div>
          </DialogHeader>

          {alertText && (
            <div className="p-4">
              <Alert
                title="info-text"
                description={alertText}
                variant="info"
                icon={
                  <Icons.InfoCircle className="size-6 text-utility-blue-500" />
                }
              />
            </div>
          )}

          <div className="flex flex-col gap-3 px-6 pb-8">
            <RadioCardKS
              heading="Add manually"
              description="Enter your bank account details manually."
              onClick={() => setConnectionMethod('MANUAL')}
              isActive={connectionMethod === 'MANUAL'}
            />
            {!disablePlaid && (
              <RadioCardKS
                heading="Connect via Plaid"
                description="Securely link your bank using Plaid to automatically sync your account information."
                onClick={() => setConnectionMethod('EXTERNAL')}
                isActive={connectionMethod === 'EXTERNAL'}
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={onContinue}
              disabled={connectionMethod === 'EXTERNAL' && !ready}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Step 2: Manual form — full-screen overlay ────────────────────────────
  return (
    <Dialog open={isShown} onOpenChange={handleClose}>
      <DialogPortal>
        <div className="fixed inset-0 z-50 bg-bg-light flex flex-col">
          <div className="bg-bg-primary border-b border-border-secondary shrink-0 flex items-center justify-center h-20 px-8">
            <div className="w-full flex items-center justify-between max-w-[1189px]">
              <div className="flex items-center gap-6">
                <img
                  src="/commenda-text-logo.svg"
                  alt="Commenda"
                  className="h-9 w-36"
                />
                <div className="bg-border-secondary shrink-0 h-9 w-px" />
                <div className="flex items-center gap-2">
                  {countryCode && (
                    <FlagComponent
                      country={countryCode}
                      countryName={countryCode}
                      _className="size-5"
                    />
                  )}
                  <span className="txt-medium-bold text-text-primary">
                    {entityName}
                  </span>
                </div>
              </div>
              <IconButton
                size="md"
                variant="default"
                onClick={handleClose}
                aria-label="Close"
                icon={<Icons.XClose className="size-5 text-text-secondary" />}
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto py-10 px-4">
            <div className="mx-auto w-full flex flex-col gap-10 max-w-xl">
              <h1 className="txt-display-xs-bold text-text-primary">
                {existingBankAccount
                  ? 'Edit bank account details'
                  : 'Add bank account details'}
              </h1>

              {/* Card */}
              <div className="border border-border-primary rounded-md overflow-hidden">
                {/* Card section header */}
                <div className="bg-bg-primary border-b border-border-primary p-6 flex flex-col gap-0.5">
                  <p className="txt-large-bold text-text-primary">
                    Bank account details
                  </p>
                  <p className="txt-small-regular text-text-secondary">
                    {existingBankAccount
                      ? `Update the bank account held by ${entityName || 'this entity'}.`
                      : `Add a bank account held by ${entityName || 'this entity'}.`}
                  </p>
                </div>

                {/* Form body — white background */}
                <div className="bg-white p-6">
                  {alertText && (
                    <div className="mb-5">
                      <Alert
                        title=""
                        description={alertText}
                        variant="info"
                        icon={
                          <Icons.InfoCircle className="size-6 text-utility-blue-500" />
                        }
                      />
                    </div>
                  )}

                  <BankAccountFormNew
                    ref={formRef}
                    corporationId={corporationId}
                    onClose={handleClose}
                    onSaveBankAccount={(bankAccountId) =>
                      onSaveBankAccount?.({
                        method: 'MANUAL',
                        bankAccountsIds: [bankAccountId],
                      })
                    }
                    addAsRemittanceAccount={addAsRemittanceAccount}
                    defaultCountry={defaultCountry}
                    showCountrySelector={showCountrySelector}
                    bankAccountId={existingBankAccount?.id}
                    initialValues={
                      existingBankAccount
                        ? {
                            accountHolderName:
                              existingBankAccount.accountHolderName,
                            country: existingBankAccount.country as string,
                            bankName: existingBankAccount.bankName,
                            currency: existingBankAccount.currency,
                            credentials: existingBankAccount.credentials,
                          }
                        : undefined
                    }
                  />
                </div>
              </div>

              {/* Save button — outside the card border */}
              <div className="flex justify-end">
                <Button
                  size="sm"
                  disabled={isSubmitting}
                  onClick={async () => {
                    setIsSubmitting(true);
                    try {
                      await formRef.current?.submit();
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                >
                  {isSubmitting ? 'Saving...' : 'Save and submit'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogPortal>
    </Dialog>
  );
};
